/**
 * Customer File — Unified Contact Timeline + Quick-Action Composer.
 *
 * Replaces the old "Customer inbox" tab as the primary centre stage.
 * Shows EVERY interaction touching a case in reverse-chronological order:
 *   • Loan funded · repayments (received + failed)
 *   • Calls (in / out / missed / VM) with recording playback
 *   • SMS (in / out)
 *   • Emails (in / out, via M365 mirror — multi-address contact graph
 *     coming in Phase 1)
 *   • Notes (handler-authored)
 *   • P2Ps (created / kept / broken)
 *   • Stage + handler changes, off-list toggles, legal docs
 *
 * Filter chips at the top let ops mute noisy categories (e.g. repayments).
 * The "Quick action" composer pinned above the feed wires straight into:
 *   📞 Aircall click-to-dial
 *   💬 Aircall SMS send
 *   📧 M365 reply / new email
 *   🗒 Note (case.notes[])
 *   🎯 Promise to Pay
 *
 * Each composed action lands as a new event at the top of the feed once
 * the backend webhook/sync confirms it — no separate "refresh" needed.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { cur, fmtMoney, isAusDesk, rl, ageingBasePath, setCollectionsRegion } from "@/lib/regionFmt";
import { toast } from "sonner";
import {
  Loader2, Phone, MessageSquare, Mail, StickyNote, Target, RefreshCw,
  PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail, Banknote, AlertOctagon,
  CheckCircle2, XCircle, FileText, ArrowRightCircle, User, Filter, RotateCcw,
  Skull, Eye, Send, MailOpen, MessageSquareReply, Ban, Trash2, Download, Printer,
  Building2, Lock, Scale, CalendarPlus, Copy, Bot, HandCoins, PauseCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CalendarDays } from "lucide-react";
import {
  getAgeingTimeline,
  aircallListUsers,
  aircallClickToDial,
  startBreathingSpace,
  endBreathingSpace,
  addAgeingNote,
  createAgeingP2P,
  getAircallUKSmsLines,
  getAgeingM365EmailFull,
  aircallSyncRecentCalls,
  syncAgeingM365History,
  refreshAgeingLoanHistory,
  deleteAgeingNote,
  deleteAgeingP2P,
  hideAgeingTimelineEvent,
  fetchAircallCallAiSummary,
  getAgeingContacts,
  slackShareEvent,
  listLegalEventTypes,
  createLegalEvent,
  listChaserSmsTemplates,
  renderChaserSmsForCase,
  previewChaserSmsForCase,
  logDockSmsIntent,
  approvePaymentPlan,
  rejectPaymentPlan,
} from "@/lib/api";
import { useAircall } from "@/contexts/AircallContext";
import { useSlack } from "@/contexts/SlackContext";
import { PaymentPlanWizard } from "./PaymentPlanWizard";
import { PayoutLetterWizard } from "./PayoutLetterWizard";
import { StatementDialog } from "./StatementDialog";
import { GenerateLegalDocDialog } from "./GenerateLegalDocWizard";
import { Receipt, FileWarning } from "lucide-react";

// ───────────────────────────────────────────────────────────────────────
// Visual config — tone + icon per event category
// ───────────────────────────────────────────────────────────────────────
export const CATEGORY_META = {
  call:             { label: "Calls",         chipTone: "bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100" },
  sms:              { label: "SMS",           chipTone: "bg-violet-50 text-violet-800 border-violet-300 hover:bg-violet-100" },
  email:            { label: "Emails",        chipTone: "bg-indigo-50 text-indigo-800 border-indigo-300 hover:bg-indigo-100" },
  note:             { label: "Coll. Notes",   chipTone: "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100" },
  legal_note:       { label: "Legal Notes",   chipTone: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300 hover:bg-fuchsia-100" },
  p2p:              { label: "P2Ps",          chipTone: "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100" },
  payment:          { label: "Payments",      chipTone: "bg-teal-50 text-teal-800 border-teal-300 hover:bg-teal-100" },
  loan:             { label: "Loan",          chipTone: "bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100" },
  document:         { label: "Documents",     chipTone: "bg-purple-50 text-purple-800 border-purple-300 hover:bg-purple-100" },
  companies_house:  { label: "Filings",       chipTone: "bg-sky-50 text-sky-800 border-sky-300 hover:bg-sky-100" },
  system:           { label: "System",        chipTone: "bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200" },
};
export const CATEGORY_ORDER = ["call", "sms", "email", "note", "legal_note", "p2p", "payment", "loan", "document", "companies_house", "system"];
// "Legal view" preset — categories muted by default on a legal file (noise, not evidence).
const LEGAL_VIEW_HIDDEN = ["system", "p2p", "payment"];

// Icon + colour by event TYPE.
// Direction-coded:
//   🔵 Blue   = outbound (we → customer)
//   🟢 Green  = inbound  (customer → us, or money in)
//   🟡 Amber  = internal (notes, voicemail to follow up)
//   🔴 Red    = risk / negative (restructure, write-off, bad debt, missed call, broken P2P, failed payment)
//   ⚪ Slate  = neutral system event (stage change, off-list, read receipts)
const EVENT_ICON = {
  loan_funded:      { icon: Banknote,           tone: "text-blue-700 bg-blue-50" },
  repayment:        { icon: Banknote,           tone: "text-emerald-700 bg-emerald-50" },
  repayment_failed: { icon: AlertOctagon,       tone: "text-rose-700 bg-rose-50" },
  call_in:          { icon: PhoneIncoming,      tone: "text-emerald-700 bg-emerald-50" },
  call_out:         { icon: PhoneOutgoing,      tone: "text-blue-700 bg-blue-50" },
  call_missed:      { icon: PhoneMissed,        tone: "text-rose-700 bg-rose-50" },
  voicemail:        { icon: Voicemail,          tone: "text-amber-700 bg-amber-50" },
  sms_in:           { icon: MessageSquareReply, tone: "text-emerald-700 bg-emerald-50" },
  sms_out:          { icon: MessageSquare,      tone: "text-blue-700 bg-blue-50" },
  email_in:         { icon: Mail,               tone: "text-emerald-700 bg-emerald-50" },
  email_out:        { icon: Send,               tone: "text-blue-700 bg-blue-50" },
  email_read_receipt:{ icon: Eye,               tone: "text-slate-500 bg-slate-100" },
  note:             { icon: StickyNote,         tone: "text-amber-700 bg-amber-50" },
  p2p_created:      { icon: Target,             tone: "text-amber-700 bg-amber-50" },
  p2p_kept:         { icon: CheckCircle2,       tone: "text-emerald-700 bg-emerald-100" },
  p2p_broken:       { icon: XCircle,            tone: "text-rose-700 bg-rose-50" },
  p2p_cancelled:    { icon: Ban,                tone: "text-slate-700 bg-slate-100" },
  p2p_rescheduled:  { icon: RefreshCw,          tone: "text-amber-700 bg-amber-100" },
  // Pre-Default Notice issued — high-risk legal step. RED.
  pdn_issued:       { icon: AlertOctagon,       tone: "text-white bg-rose-600" },
  stage_change:     { icon: ArrowRightCircle,   tone: "text-slate-600 bg-slate-100" },
  handler_change:   { icon: User,               tone: "text-slate-600 bg-slate-100" },
  capital_at_risk_change: { icon: AlertOctagon, tone: "text-orange-700 bg-orange-50" },
  ld_status_push:        { icon: RefreshCw,     tone: "text-emerald-700 bg-emerald-50" },
  ld_status_push_failed: { icon: AlertOctagon,  tone: "text-rose-700 bg-rose-50" },
  loan_restructured:{ icon: RotateCcw,          tone: "text-rose-700 bg-rose-50" },
  bad_debt_escalated:{ icon: AlertOctagon,      tone: "text-rose-700 bg-rose-50" },
  loan_written_off: { icon: Skull,              tone: "text-rose-700 bg-rose-50" },
  off_list_added:   { icon: AlertOctagon,       tone: "text-slate-600 bg-slate-100" },
  // E-signature envelopes — DocuSign workflow
  esign_sent:       { icon: Send,               tone: "text-blue-700 bg-blue-50" },
  esign_signed:     { icon: CheckCircle2,       tone: "text-emerald-700 bg-emerald-50" },
  esign_declined:   { icon: XCircle,            tone: "text-rose-700 bg-rose-50" },
  esign_voided:     { icon: Ban,                tone: "text-slate-700 bg-slate-100" },

  // Document generation — DocuPilot (we generate + post legal notices)
  // and LoanDisk's built-in document generator (PDNs, summaries, etc.)
  docupilot_generated: { icon: FileText,        tone: "text-purple-700 bg-purple-50" },
  loandisk_document:   { icon: FileText,        tone: "text-emerald-700 bg-emerald-50" },

  // Payment plans — created + emailed from the Customer File quick action
  payment_plan_created: { icon: HandCoins,      tone: "text-indigo-700 bg-indigo-50" },
  payout_letter_issued: { icon: Receipt,        tone: "text-emerald-700 bg-emerald-50" },
  statement_issued:     { icon: FileText,       tone: "text-emerald-700 bg-emerald-50" },
  payment_plan_sent:    { icon: Send,           tone: "text-indigo-700 bg-indigo-50" },
  payment_plan_approved: { icon: CheckCircle2,  tone: "text-emerald-700 bg-emerald-50" },
  payment_plan_rejected: { icon: XCircle,       tone: "text-rose-700 bg-rose-50" },

  // Companies House filings — sourced via the dedicated CH integration
  ch_filing:               { icon: Building2,    tone: "text-sky-700 bg-sky-50" },
  ch_director_appointed:   { icon: User,         tone: "text-emerald-700 bg-emerald-50" },
  ch_director_resigned:    { icon: User,         tone: "text-rose-700 bg-rose-50" },
  ch_address_change:       { icon: ArrowRightCircle, tone: "text-amber-700 bg-amber-50" },
  ch_accounts:             { icon: FileText,     tone: "text-sky-700 bg-sky-50" },
  ch_confirmation:         { icon: CheckCircle2, tone: "text-sky-700 bg-sky-50" },
  ch_charge:               { icon: Lock,         tone: "text-purple-700 bg-purple-50" },
  ch_gazette:              { icon: AlertOctagon, tone: "text-white bg-rose-600" },
  ch_incorporation:        { icon: Building2,    tone: "text-slate-700 bg-slate-100" },

  // Legal notes — imported from Airtable and portal-authored. The
  // backend emits types as `legal_<action_type>` (e.g.
  // `legal_correspondence_sent`, `legal_lba_sent`, `legal_hearing_booked`).
  // We use the parent category (`legal_note`) for visual coding via
  // `getEventIconAndTone` fallback so we don't need to enumerate every
  // possible action_type here.

  // Loan history events — emitted by the backend's loan-history walk
  // (full disbursement → restructure chain → payments → fully-paid)
  loan_disbursed:           { icon: Banknote,     tone: "text-blue-700  bg-blue-50" },
  loan_restructured_from_chain: { icon: RotateCcw, tone: "text-rose-700 bg-rose-50" },
  loan_payment:             { icon: Banknote,     tone: "text-emerald-700 bg-emerald-50" },
  loan_fully_paid:          { icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-100" },
};

// Fallback for `legal_*` event types — they all render with the same
// pill (fuchsia, scale icon) so the category visually contrasts with
// the amber Collections "Notes" category in the same timeline.
const LEGAL_NOTE_VISUAL = { icon: Scale, tone: "text-fuchsia-700 bg-fuchsia-50" };

export function getEventVisual(event) {
  const explicit = EVENT_ICON[event?.type];
  if (explicit) return explicit;
  if (typeof event?.type === "string" && event.type.startsWith("legal_")) {
    return LEGAL_NOTE_VISUAL;
  }
  return null;
}

export const relativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1)   return "just now";
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// ── Deletable events — only manual entries should be removable from the
// timeline. External comms (calls/SMS/emails) and system events (stage
// changes, loan funded) are read-only history. Returns null if not
// deletable.
function deletableInfo(event) {
  if (!event?.id) return null;
  if (event.id.startsWith("note-")) {
    return { kind: "note", id: event.id.slice("note-".length), label: "note" };
  }
  if (event.id.startsWith("p2p-")) {
    // p2p-{id}-{state}  →  extract the p2p id (everything between p2p- and the last -state)
    const stripped = event.id.slice("p2p-".length);
    const lastDash = stripped.lastIndexOf("-");
    const p2pId = lastDash > 0 ? stripped.slice(0, lastDash) : stripped;
    return { kind: "p2p", id: p2pId, label: "P2P + all its history" };
  }
  // Every other event type is soft-hidden via the timeline/hide endpoint —
  // the underlying record stays intact in its source collection (Aircall
  // log, M365 archive, LoanDisk cache, Companies House filings) so an
  // admin can restore it later. Uses the label to make the confirmation
  // dialog copy human-readable per category.
  const CATEGORY_LABEL = {
    call: "call log entry",
    sms:  "SMS",
    email: "email",
    payment: "payment record",
    loan: "loan history entry",
    document: "document",
    companies_house: "Companies House filing",
    legal_note: "legal note",
    system: "system event",
  };
  return {
    kind:  "hide",
    id:    event.id,
    label: CATEGORY_LABEL[event.category] || "event",
  };
}

// ── Export helpers ─────────────────────────────────────────────────────
// CSV — opens cleanly in Excel/Numbers/Sheets. We keep it as CSV rather
// than XLSX so we avoid pulling in a 400KB library for one button.
function exportEventsAsCsv(caseRow, events, audienceLabel = "all") {
  const headers = ["When", "Category", "Audience", "Type", "Actor", "Title", "Summary", "Phone / extra"];
  const escape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = events.map(e => [
    e.at || "",
    e.category || "",
    e.audience || "",
    e.type || "",
    e.actor || "",
    (e.title || "").replace(/\s+/g, " ").trim(),
    (e.summary || e.body || "").replace(/\s+/g, " ").trim().slice(0, 500),
    e.meta?.phone || e.meta?.from || "",
  ].map(escape).join(","));
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug = caseRow.customer_name?.replace(/[^a-z0-9]+/gi, "_") || "timeline";
  a.download = `${caseRow.case_id}-${slug}-${audienceLabel}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// PDF via browser print — write a print-friendly HTML doc in a new window
// and trigger window.print(). Users pick "Save as PDF" in the print dialog.
// Court-admissible timeline PDF.
//
// Header carries the issuing entity (Morley Capital Ltd T/A SimplyFunded),
// registered address, phone, email and website so the printed copy stands
// alone as evidence of contact attempts when produced in proceedings.
// The logo is loaded from the frontend's /brand/ folder (served by the
// same origin as the portal) and we delay `window.print()` until the image
// has actually rendered, otherwise the print preview can fire before the
// header has paint and the user gets a logo-less first page.
const COMPANY_HEADER = {
  legalName:   "Morley Capital Ltd T/A SimplyFunded",
  addressLine: "S10.B Evolve Business Centre, Cygnet Way, Houghton-Le-Spring, DH4 5QY, United Kingdom",
  phone:       "+44 1642 057 445",
  email:       "accounts@simplyfunded.co.uk",
  website:     "simplyfunded.co.uk",
};

function exportEventsAsPdf(caseRow, events, audienceLabel = "all") {
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) {
    toast.error("Couldn't open print preview — allow pop-ups for the portal");
    return;
  }
  const escape = (v) => String(v ?? "").replace(/[&<>"']/g, c => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
  const logoUrl = `${window.location.origin}/brand/simplyfunded-logo.png`;
  const rows = events.map(e => `
    <tr>
      <td class="when">${escape(e.at ? new Date(e.at).toLocaleString("en-GB") : "")}</td>
      <td class="cat ${escape(e.category)}">${escape(e.category)}</td>
      <td class="title"><strong>${escape(e.title)}</strong>${e.actor ? ` <span class="actor">· ${escape(e.actor)}</span>` : ""}<br/><span class="summary">${escape(e.summary || "")}</span></td>
    </tr>
  `).join("");
  w.document.write(`<!DOCTYPE html>
<html><head>
  <meta charset="utf-8">
  <title>Timeline — ${escape(caseRow.customer_name || caseRow.case_id)}</title>
  <style>
    body { font: 11px -apple-system, system-ui, sans-serif; color: #1e293b; margin: 24px; }
    .letterhead { display: flex; align-items: center; gap: 20px; padding-bottom: 12px; border-bottom: 2px solid #1e293b; margin-bottom: 14px; }
    .letterhead img { width: 160px; height: auto; flex-shrink: 0; }
    .letterhead .ent { flex: 1; }
    .letterhead .ent .name { font-size: 14px; font-weight: 700; color: #0f172a; letter-spacing: 0.2px; }
    .letterhead .ent .addr { font-size: 10px; color: #334155; margin-top: 2px; line-height: 1.4; }
    .letterhead .ent .contact { font-size: 10px; color: #334155; margin-top: 4px; }
    .letterhead .ent .contact span + span:before { content: " · "; color: #94a3b8; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    .sub { color: #64748b; font-size: 11px; margin-bottom: 14px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 6px 8px; vertical-align: top; border-bottom: 1px solid #e2e8f0; }
    th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; background: #f8fafc; }
    .when { width: 130px; font-variant-numeric: tabular-nums; color: #64748b; }
    .cat  { width: 78px; text-transform: capitalize; font-size: 9px; font-weight: 600; }
    .cat.call { color: #1d4ed8; }
    .cat.sms { color: #7c3aed; }
    .cat.email { color: #4338ca; }
    .cat.payment { color: #047857; }
    .cat.note { color: #92400e; }
    .cat.p2p { color: #be185d; }
    .actor { color: #64748b; }
    .summary { color: #475569; font-size: 10px; display: block; margin-top: 1px; }
    @media print {
      @page {
        margin: 18mm;
        size: A4;
        /* Page X of N — bottom-right footer on every printed page. */
        @bottom-right {
          content: "Page " counter(page) " of " counter(pages);
          font: 9px -apple-system, system-ui, sans-serif;
          color: #64748b;
        }
      }
      body { margin: 0; }
      .letterhead { break-inside: avoid; }
      thead { display: table-header-group; }
      tr { break-inside: avoid; }
    }
  </style>
</head><body>
  <div class="letterhead">
    <img id="brand-logo" src="${escape(logoUrl)}" alt="" onerror="this.style.display='none'" />
    <div class="ent">
      <div class="name">${escape(COMPANY_HEADER.legalName)}</div>
      <div class="addr">${escape(COMPANY_HEADER.addressLine)}</div>
      <div class="contact">
        <span>${escape(COMPANY_HEADER.phone)}</span>
        <span>${escape(COMPANY_HEADER.email)}</span>
        <span>${escape(COMPANY_HEADER.website)}</span>
      </div>
    </div>
  </div>
  <h1>Contact Timeline — ${escape(caseRow.customer_name || "Customer")}</h1>
  <div class="sub">
    ${escape(caseRow.loan_application_id || "")} · ${events.length} event${events.length === 1 ? "" : "s"} ·
    ${audienceLabel === "external" ? "External-friendly view (court-ready)" :
      audienceLabel === "internal" ? "Internal ops view (incl. handler notes &amp; status changes)" :
      "Combined view (internal + external)"} ·
    exported ${new Date().toLocaleString("en-GB")}
  </div>
  <table>
    <thead><tr><th>When</th><th>Category</th><th>Detail</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <script>
    // Wait for the brand logo to load (or fail) before opening the print
    // dialog so the first page always carries the letterhead.
    (function () {
      const img = document.getElementById("brand-logo");
      const go = () => setTimeout(() => window.print(), 150);
      if (!img || img.complete) { go(); return; }
      img.addEventListener("load", go);
      img.addEventListener("error", go);  // fall back gracefully if blocked
      setTimeout(go, 1500);                // safety net
    })();
  </script>
</body></html>`);
  w.document.close();
}

// ───────────────────────────────────────────────────────────────────────
// Main component
// ───────────────────────────────────────────────────────────────────────
export function ContactTimelineView({ caseRow, onChanged, isLegalMode = false }) {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Filter state — ALL categories visible by default. Ops toggles chips
  // off when they want to mute a noisy bucket (e.g. repayments on big files).
  // Legal mode (Dean, 8 Oct 2026) opens on the "Legal view" preset instead.
  const [hidden, setHidden] = useState(() => (isLegalMode ? new Set(LEGAL_VIEW_HIDDEN) : new Set()));
  const isAllPreset = hidden.size === 0;
  const isLegalPreset = hidden.size === LEGAL_VIEW_HIDDEN.length && LEGAL_VIEW_HIDDEN.every((c) => hidden.has(c));
  // Audience filter — both internal AND external visible by default. Ops
  // unticks one to get a court-ready or ops-only view. Tick both = "all".
  const [audienceSelected, setAudienceSelected] = useState(() => new Set(["internal", "external"]));
  const [countsByAudience, setCountsByAudience] = useState({ internal: 0, external: 0 });
  const [refreshTick, setRefreshTick] = useState(0);
  const aircall = useAircall();

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setErr(null);
      const r = await getAgeingTimeline(caseRow.case_id, { limit: 500 });
      setEvents(r.events || []);
      setCounts(r.counts_by_category || {});
      setCountsByAudience(r.counts_by_audience || { internal: 0, external: 0 });
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow.case_id]);

  useEffect(() => { reload(); }, [reload, refreshTick]);

  // Auto-refresh whenever the Aircall SDK fires a call event — picks up
  // outbound dials, incoming calls, and call-ended events. For
  // call-related events we first ping the backend to fetch the most
  // recent Aircall calls into our local mirror so the timeline actually
  // has the new entry to show.
  useEffect(() => {
    if (!aircall?.refreshTick?.at) return;
    const kind = aircall.refreshTick.kind;
    const callRelated = kind === "call_ended" || kind === "outgoing_answered" || kind === "incoming_call";
    if (callRelated) {
      aircallSyncRecentCalls(10)
        .catch(() => {})          // best-effort — timeline still reloads even if sync fails
        .finally(() => setRefreshTick(t => t + 1));
    } else {
      setRefreshTick(t => t + 1);
    }
  }, [aircall?.refreshTick?.at, aircall?.refreshTick?.kind]);

  // Shift+click → solo this category (hide all others). Shift+click the same
  // soloed chip again → restore everything. Plain click still toggles single.
  const handleChipClick = (cat, e) => {
    const allCats = CATEGORY_ORDER.filter(c => (counts[c] || 0) > 0);
    if (e?.shiftKey) {
      const othersHidden = allCats.filter(c => c !== cat);
      const isAlreadySoloed =
        !hidden.has(cat) &&
        othersHidden.every(c => hidden.has(c));
      if (isAlreadySoloed) {
        // Restore: show all
        setHidden(new Set());
      } else {
        // Solo: hide every other category
        setHidden(new Set(othersHidden));
      }
      return;
    }
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const visible = useMemo(() => {
    const filtered = events.filter(e =>
      !hidden.has(e.category) &&
      audienceSelected.has(e.audience || "external")
    );
    // Legal view: consecutive identical system lines on the same day collapse into one "×N" row.
    if (!isLegalMode || hidden.size === 0) return filtered;
    const out = [];
    for (const e of filtered) {
      const prev = out[out.length - 1];
      if (prev && e.category === "system" && prev.category === "system" && prev.title === e.title && deskDayKey(prev.at) === deskDayKey(e.at)) {
        out[out.length - 1] = { ...prev, _dup: (prev._dup || 1) + 1 };
      } else {
        out.push(e);
      }
    }
    return out;
  }, [events, hidden, audienceSelected, isLegalMode]);

  const toggleAudience = (key) => {
    setAudienceSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Don't allow the user to deselect both — keep at least one
        if (next.size === 1) return next;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const setAudienceExact = (keys) => setAudienceSelected(new Set(keys));

  // Group by day for the side rail
  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const e of visible) {
      const day = deskDayKey(e.at);
      if (day !== lastDay) {
        out.push({ kind: "day", day });
        lastDay = day;
      }
      out.push({ kind: "event", event: e });
    }
    return out;
  }, [visible]);

  return (
    <div className="bg-white rounded-lg border shadow-sm flex flex-col" data-testid="contact-timeline-view">
      {/* Quick-action composer */}
      <QuickActionComposer
        caseRow={caseRow}
        onAdded={() => { setRefreshTick(t => t + 1); onChanged?.(); }}
      />

      {/* Filter chips strip — two rows: audience (parent) + category (nested) */}
      <div className="px-4 py-2 border-t border-b bg-slate-50" data-testid="timeline-filter-chips">

        {/* Row 1 — Audience (parent filter) */}
        <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          {isLegalMode && (
            <div className="flex items-center rounded-full border bg-white p-0.5 mr-2" data-testid="timeline-preset-toggle">
              <button
                type="button"
                onClick={() => setHidden(new Set(LEGAL_VIEW_HIDDEN))}
                className={`h-6 px-2.5 rounded-full text-[11px] font-medium transition-colors ${isLegalPreset ? "bg-red-700 text-white" : "text-slate-600 hover:text-slate-900"}`}
                aria-pressed={isLegalPreset}
                title="Legal view — hides system, P2P and payment noise; repeated system lines collapse"
                data-testid="timeline-preset-legal"
              >
                Legal view
              </button>
              <button
                type="button"
                onClick={() => setHidden(new Set())}
                className={`h-6 px-2.5 rounded-full text-[11px] font-medium transition-colors ${isAllPreset ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                aria-pressed={isAllPreset}
                title="All — the full Collections timeline, nothing hidden"
                data-testid="timeline-preset-all"
              >
                All
              </button>
              {!isLegalPreset && !isAllPreset && (
                <span className="px-2 text-[10px] text-slate-500" data-testid="timeline-preset-custom">custom</span>
              )}
            </div>
          )}
          <span
            className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mr-1"
            title="Pick which view: internal (ops chatter incl. notes / stage / LD push), external (court-ready customer-facing events), or both."
          >
            View
          </span>
          {[
            { key: "internal", label: "Internal", count: countsByAudience.internal || 0, tone: "bg-orange-50 text-orange-900 border-orange-300", activeTone: "bg-orange-100 text-orange-900 border-orange-400 ring-1 ring-orange-300" },
            { key: "external", label: "External-friendly", count: countsByAudience.external || 0, tone: "bg-emerald-50 text-emerald-900 border-emerald-300", activeTone: "bg-emerald-100 text-emerald-900 border-emerald-400 ring-1 ring-emerald-300" },
          ].map(opt => {
            const active = audienceSelected.has(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => toggleAudience(opt.key)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? opt.activeTone : "bg-white text-slate-400 border-slate-200 line-through"}`}
                data-testid={`timeline-audience-${opt.key}`}
                title={active ? `Click to hide ${opt.label.toLowerCase()} events` : `Click to show ${opt.label.toLowerCase()} events`}
              >
                {opt.label} <span className="ml-1 tabular-nums opacity-80">{opt.count}</span>
              </button>
            );
          })}
          {audienceSelected.size !== 2 && (
            <button
              type="button"
              onClick={() => setAudienceExact(["internal", "external"])}
              className="px-2 py-1 rounded-full text-[11px] font-medium text-slate-600 hover:text-slate-900 hover:bg-white border border-dashed border-slate-300"
              title="Show both audiences"
              data-testid="timeline-audience-reset"
            >
              Show all
            </button>
          )}
        </div>

        {/* Row 2 — Categories (nested under audience filter) */}
        <div className="flex items-center gap-1.5 flex-wrap pl-5">
          <span
            className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mr-1"
            title="Click to toggle a category. Shift+click to solo (show only this one)."
          >
            Types
          </span>
          {CATEGORY_ORDER.map(cat => {
            const meta = CATEGORY_META[cat];
            const count = counts[cat] || 0;
            const active = !hidden.has(cat);
            const activeCount = CATEGORY_ORDER.filter(
              c => (counts[c] || 0) > 0 && !hidden.has(c),
            ).length;
            const isSoloed = active && activeCount === 1;
            return (
              <button
                key={cat}
                onClick={(e) => handleChipClick(cat, e)}
                disabled={count === 0}
                title={
                  active
                    ? "Click to hide · Shift+click to solo"
                    : "Click to show · Shift+click to solo"
                }
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  active
                    ? `${meta.chipTone} ${isSoloed ? "ring-2 ring-offset-1 ring-slate-900" : ""}`
                    : "bg-white text-slate-400 border-slate-200 line-through"
                }`}
                data-testid={`timeline-filter-${cat}`}
              >
                {meta.label} <span className="ml-1 tabular-nums opacity-80">{count}</span>
              </button>
            );
          })}
          {hidden.size > 0 && (
            <button
              type="button"
              onClick={() => setHidden(new Set())}
              className="px-2 py-1 rounded-full text-[11px] font-medium text-slate-600 hover:text-slate-900 hover:bg-white border border-dashed border-slate-300"
              title="Restore all categories"
              data-testid="timeline-filter-reset"
            >
              Show all
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground" data-testid="timeline-visible-count">
              {visible.length} of {events.length} events
            </span>
          {(() => {
            const audienceLabel = audienceSelected.size === 2 ? "all" : (audienceSelected.has("external") ? "external" : "internal");
            return (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" data-testid="timeline-export-trigger">
                    <Download className="w-3.5 h-3.5 mr-1" /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="text-xs">
                  <DropdownMenuItem onClick={() => exportEventsAsCsv(caseRow, visible, audienceLabel)} data-testid="timeline-export-csv">
                    <FileText className="w-3.5 h-3.5 mr-2" /> Excel / CSV ({audienceLabel})
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportEventsAsPdf(caseRow, visible, audienceLabel)} data-testid="timeline-export-pdf">
                    <Printer className="w-3.5 h-3.5 mr-2" /> PDF — {audienceLabel}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })()}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={async () => {
              // Item 2: "Sync Timeline" auto-loads from ALL sources — not
              // just Aircall. Fan out in parallel: Aircall calls (60-day),
              // M365 emails (last 200), and loan-history cache-bust (busts
              // the 6h LoanDisk repayments cache too, so the next timeline
              // load picks up fresh restructures + Companies House filings).
              const tid = toast.loading("Timeline sync in progress…");
              try {
                await Promise.allSettled([
                  aircallSyncRecentCalls(60),
                  syncAgeingM365History(caseRow.case_id, { limit: 200 }),
                  refreshAgeingLoanHistory(caseRow.case_id),
                ]);
                toast.success("Timeline synced", { id: tid });
              } catch {
                toast.dismiss(tid);
              }
              setRefreshTick(t => t + 1);
            }}
            title="Sync Aircall calls + M365 emails + LoanDisk history, then refresh the timeline"
            data-testid="timeline-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        </div>
      </div>

      {/* Feed */}
      <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 360px)" }}>
        {loading && events.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading timeline…
          </div>
        ) : err ? (
          <div className="m-3 p-3 rounded-md border border-rose-200 bg-rose-50 text-sm text-rose-900">
            <div className="font-semibold mb-0.5">Could not load timeline</div>
            <div className="text-xs">{err}</div>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {events.length === 0
              ? "No events on this case yet — start a conversation using the composer above."
              : "All event categories hidden — re-enable a filter chip to see events."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {grouped.map((item, idx) => item.kind === "day" ? (
              <DayHeader key={`day-${item.day}-${idx}`} day={item.day} />
            ) : (
              <EventRow
                key={item.event.id}
                event={item.event}
                caseId={caseRow.case_id}
                onDeleted={(deletedEvent) => {
                  // Optimistic update — drop the row immediately so ops get
                  // instant feedback. Then schedule a soft refresh so any
                  // ripple-effect events (e.g. P2P delete removes its sibling
                  // p2p_created note auto-row) also disappear.
                  setEvents(prev => prev.filter(e => {
                    if (e.id === deletedEvent.id) return false;
                    // For p2p deletion, also drop other rows referencing the
                    // same p2p id (created/kept/broken/rescheduled siblings).
                    if (deletedEvent.id.startsWith("p2p-")) {
                      const stripped = deletedEvent.id.slice("p2p-".length);
                      const dash = stripped.lastIndexOf("-");
                      const p2pId = dash > 0 ? stripped.slice(0, dash) : stripped;
                      if (e.id.startsWith(`p2p-${p2pId}-`)) return false;
                      if (e.category === "note" && e.meta?.p2p_id === p2pId) return false;
                    }
                    return true;
                  }));
                  // Decrement the count chip too
                  setCounts(prev => ({
                    ...prev,
                    [deletedEvent.category]: Math.max(0, (prev[deletedEvent.category] || 0) - 1),
                  }));
                  setRefreshTick(t => t + 1);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Calendar day in the desk's timezone (UK: Europe/London, AUS: Australia/Sydney).
// Grouping on the raw UTC date filed a 00:30 BST P2P note under "yesterday" (Emma, Sep 2026).
const _deskTz = () => (isAusDesk() ? "Australia/Sydney" : "Europe/London");
export function deskDayKey(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  if (!/[T ]\d{2}:\d{2}/.test(String(iso))) return String(iso).slice(0, 10);   // date-only → keep as-is
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: _deskTz(), year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function DayHeader({ day }) {
  const d = day ? new Date(day + "T12:00:00") : null;
  const label = d
    ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : "Unknown date";
  return (
    <div className="sticky top-0 bg-slate-50/95 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 border-b z-10">
      {label}
    </div>
  );
}

// Slack mark used in the per-event "Share" button. Inline SVG so we don't
// pull in a brand-icon set just for one glyph.
const SlackIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#E01E5A" d="M5.04 15.41A2.52 2.52 0 1 1 2.52 12.9h2.52v2.51z" />
    <path fill="#E01E5A" d="M6.31 15.41a2.52 2.52 0 1 1 5.04 0v6.27a2.52 2.52 0 1 1-5.04 0v-6.27z" />
    <path fill="#36C5F0" d="M8.83 5.04a2.52 2.52 0 1 1 2.52-2.52v2.52H8.83z" />
    <path fill="#36C5F0" d="M8.83 6.31a2.52 2.52 0 1 1 0 5.04H2.56a2.52 2.52 0 1 1 0-5.04h6.27z" />
    <path fill="#2EB67D" d="M18.96 8.83a2.52 2.52 0 1 1 2.52 2.52h-2.52V8.83z" />
    <path fill="#2EB67D" d="M17.69 8.83a2.52 2.52 0 1 1-5.04 0V2.56a2.52 2.52 0 1 1 5.04 0v6.27z" />
    <path fill="#ECB22E" d="M15.17 18.96a2.52 2.52 0 1 1-2.52 2.52v-2.52h2.52z" />
    <path fill="#ECB22E" d="M15.17 17.69a2.52 2.52 0 1 1 0-5.04h6.27a2.52 2.52 0 1 1 0 5.04h-6.27z" />
  </svg>
);

// Per-event "Share to Slack" popover. Loads channels lazily from the
// SlackContext (already cached across the app), pre-fills the default
// channel, lets ops add an optional note above the event card.
function ShareToSlackButton({ caseId, event }) {
  const slack = useSlack();
  const [open, setOpen]       = useState(false);
  const [channel, setChannel] = useState("");
  const [note, setNote]       = useState("");
  const [sending, setSending] = useState(false);

  if (!slack) return null;
  const { status, channels, loadingChannels, channelsError, reloadChannels } = slack;
  const isConfigured = status?.configured;

  // Pre-fill the default channel the first time the popover opens
  const effectiveChannel = channel || status?.default_channel || "";

  const handleOpen = (next) => {
    setOpen(next);
    if (next && channels.length === 0 && !loadingChannels) {
      reloadChannels();
    }
  };

  const handleShare = async () => {
    if (!effectiveChannel) {
      toast.error("Pick a channel first");
      return;
    }
    setSending(true);
    try {
      await slackShareEvent({
        channel:  effectiveChannel,
        case_id:  caseId,
        event_id: event.id,
        note:     note.trim() || null,
        actor:    "Ops",
      });
      toast.success("Shared to Slack");
      setOpen(false);
      setNote("");
    } catch (e) {
      toast.error("Slack share failed: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setSending(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-slate-900 hover:bg-slate-100 p-0.5 rounded"
          title={isConfigured ? "Share to Slack" : "Slack not configured"}
          data-testid={`timeline-event-share-slack-${event.type}`}
        >
          <SlackIcon className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        {!isConfigured ? (
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-700">Slack not connected</div>
            <div className="text-xs text-muted-foreground">
              Ask an admin to add the Slack bot token in Admin Settings.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="font-semibold text-slate-800 text-sm">Share to Slack</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {event.title}
            </div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground block mt-1">
              Channel
            </label>
            <select
              value={effectiveChannel}
              onChange={(e) => setChannel(e.target.value)}
              disabled={loadingChannels}
              className="w-full text-sm border rounded-md px-2 py-1.5 bg-white"
              data-testid="share-slack-channel"
            >
              <option value="">— Pick a channel —</option>
              {channels.map(c => (
                <option key={c.id} value={c.id}>
                  {c.is_private ? "🔒" : "#"}{c.name}
                </option>
              ))}
            </select>
            {channelsError && (
              <div className="text-[11px] text-rose-600">{channelsError}</div>
            )}
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground block mt-1">
              Note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context for the team…"
              rows={2}
              maxLength={500}
              className="w-full text-sm border rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              data-testid="share-slack-note"
            />
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setOpen(false)}
                disabled={sending}
                data-testid="share-slack-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 px-3 text-xs"
                onClick={handleShare}
                disabled={sending || !effectiveChannel}
                data-testid="share-slack-send"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                Share
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function PlanApprovalActions({ event, caseId, onDecided }) {
  const [busy, setBusy] = useState(false);
  const decide = async (approve) => {
    try {
      setBusy(true);
      if (approve) await approvePaymentPlan(caseId, event.meta.plan_id);
      else await rejectPaymentPlan(caseId, event.meta.plan_id);
      toast.success(approve ? "✅ Payment plan approved" : "Plan rejected");
      onDecided?.(event);   // drops the stale row + soft-refreshes the feed
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-1.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      {(event.meta?.breaches || []).length > 0 && (
        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 truncate max-w-[280px]"
              title={(event.meta.breaches || []).join("\n")}>
          {event.meta.breaches[0]}{event.meta.breaches.length > 1 ? ` +${event.meta.breaches.length - 1}` : ""}
        </span>
      )}
      <Button size="sm" className="h-6 text-[11px] px-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy}
              onClick={() => decide(true)} data-testid={`plan-approve-btn-${event.meta.plan_id}`}>
        <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
      </Button>
      <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 text-rose-700 border-rose-300 hover:bg-rose-50" disabled={busy}
              onClick={() => decide(false)} data-testid={`plan-reject-btn-${event.meta.plan_id}`}>
        <XCircle className="w-3 h-3 mr-1" /> Reject
      </Button>
    </div>
  );
}


function EventRow({ event, caseId, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [emailHtml, setEmailHtml] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailErr, setEmailErr] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const deletable = deletableInfo(event);
  const baseMeta = getEventVisual(event) || { icon: StickyNote, tone: "text-slate-600 bg-slate-100" };

  const handleDelete = async () => {
    if (!deletable) return;
    try {
      setDeleting(true);
      if (deletable.kind === "note") {
        await deleteAgeingNote(caseId, deletable.id);
      } else if (deletable.kind === "p2p") {
        await deleteAgeingP2P(caseId, deletable.id);
      } else if (deletable.kind === "hide") {
        // Soft-hide — external audit trail row (Aircall SMS, M365 email,
        // LoanDisk doc, etc.). The underlying record stays in its source
        // collection and can be restored later.
        await hideAgeingTimelineEvent(caseId, deletable.id);
      }
      toast.success(deletable.kind === "hide" ? `Hidden ${deletable.label}` : `Deleted ${deletable.label}`);
      setDeleteOpen(false);
      onDeleted?.(event);
    } catch (e) {
      toast.error("Delete failed: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setDeleting(false);
    }
  };

  // Stage changes into a risk state are visually escalated to red so ops
  // can spot them at a glance ("Capital at Risk", "Arrears", "Legal").
  const RISK_STAGES = new Set(["capital_at_risk", "arrears", "legal", "default", "delinquent"]);
  const isRiskStage = event.type === "stage_change" && RISK_STAGES.has((event.meta?.stage || "").toLowerCase());
  const meta = isRiskStage
    ? { ...baseMeta, tone: "text-rose-700 bg-rose-50" }
    : baseMeta;

  const Icon = meta.icon;
  const isEmail = event.category === "email";
  const isSms = event.category === "sms";
  const isSmsOut = event.type === "sms_out";
  const isSmsIn  = event.type === "sms_in";
  const isCall = event.category === "call";
  const callHasAiSummary = isCall && !!event.meta?.ai_summary;
  const callHasNoAiData  = isCall && !!event.meta?.ai_no_insights;
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [fetchingSummary, setFetchingSummary] = useState(false);
  // Local override so a fresh "no AI data" response hides the button
  // immediately (before the timeline refetches and picks up the DB flag).
  const [locallyNoAiData, setLocallyNoAiData] = useState(false);
  const isReadReceipt = event.type === "email_read_receipt";
  const hasBody = !!(event.body || event.meta?.recording || event.meta?.web_link || isEmail || isSms || event.summary);

  // Lazy-load the FULL email HTML body the first time the row is expanded.
  // If Graph returns 404 (archived id whose folder moved) we degrade gracefully
  // to the cached `summary` preview + the meta.web_link "Open in Outlook" link.
  useEffect(() => {
    if (!expanded || !isEmail || isReadReceipt || emailHtml || emailLoading) return;
    const msgId = event.meta?.message_id;
    if (!msgId) return;
    (async () => {
      try {
        setEmailLoading(true);
        setEmailErr(null);
        const r = await getAgeingM365EmailFull(caseId, msgId);
        const html =
          r?.message?.body?.content ||
          r?.message?.body?.contentText ||
          "(empty body)";
        setEmailHtml(html);
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail || "";
        // 404 / ErrorItemNotFound = archived id no longer resolvable in Graph.
        // Show the cached preview instead of a hard error.
        if (status === 404 || status === 502 || /ItemNotFound|not found/i.test(detail)) {
          setEmailErr("archived");
        } else {
          setEmailErr(detail || e.message || "Could not load email");
        }
      } finally {
        setEmailLoading(false);
      }
    })();
  }, [expanded, isEmail, emailHtml, emailLoading, event.meta, caseId]);

  return (
    <>
    <div
      className="px-4 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer group"
      onClick={() => hasBody && setExpanded(v => !v)}
      data-testid={`timeline-event-${event.type}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${meta.tone}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-slate-800">{event.title}</span>
            {event._dup > 1 && (
              <span className="text-[10px] px-1 rounded border bg-slate-100 text-slate-600 tabular-nums" title={`Repeated ${event._dup} times on this day — collapsed in Legal view`} data-testid="timeline-event-dup-badge">×{event._dup}</span>
            )}
            {event.actor && <span className="text-muted-foreground">· {event.actor}</span>}
            <span className="ml-auto text-[11px] text-muted-foreground tabular-nums" title={event.at}>
              {relativeTime(event.at)}
            </span>
            <ShareToSlackButton caseId={caseId} event={event} />
            {deletable && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
                className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 p-0.5 rounded transition-colors"
                title={deletable.kind === "hide" ? `Hide this ${deletable.label} from the timeline` : `Delete this ${deletable.label}`}
                data-testid={`timeline-event-delete-${event.type}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {event.summary && !expanded && (
            <div className="text-xs text-slate-600 mt-0.5 truncate">
              {event.summary}
              {event.meta?.file_url && (
                <a
                  href={event.meta.file_url} target="_blank" rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="ml-2 text-blue-600 hover:underline"
                  data-testid={`timeline-event-open-doc-${event.type}`}
                >
                  Open document ↗
                </a>
              )}
            </div>
          )}
          {event.type === "payment_plan_created" && event.meta?.status === "pending_approval" && (
            <PlanApprovalActions event={event} caseId={caseId} onDecided={onDeleted} />
          )}
          {expanded && (
            <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-2">
              {/* Email recipients header */}
              {isEmail && event.meta && (
                <div className="text-[11px] text-slate-500 space-y-0.5">
                  {event.meta.from && <div><span className="font-medium">From:</span> {event.meta.from}</div>}
                  {event.meta.to?.length > 0 && <div><span className="font-medium">To:</span> {event.meta.to.join(", ")}</div>}
                  {event.meta.subject && <div><span className="font-medium">Subject:</span> {event.meta.subject}</div>}
                  {event.meta.folder && <div className="text-[10px] uppercase tracking-wide text-slate-400">Folder: {event.meta.folder}</div>}
                </div>
              )}

              {/* Full email body — lazy-loaded HTML */}
              {isEmail && (
                emailLoading ? (
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading email…
                  </div>
                ) : emailErr === "archived" ? (
                  <div className="space-y-1.5">
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      Archived email — full body no longer in Outlook. Showing cached preview:
                    </div>
                    {event.summary && (
                      <div className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-2">
                        {event.summary}
                      </div>
                    )}
                  </div>
                ) : emailErr ? (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                    Could not load email body: {emailErr}
                  </div>
                ) : emailHtml ? (
                  <div
                    className="prose prose-sm max-w-none text-xs bg-white border border-slate-200 rounded p-2 max-h-96 overflow-y-auto"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: emailHtml }}
                  />
                ) : event.summary ? (
                  <div className="text-xs text-slate-600 italic">{event.summary}</div>
                ) : null
              )}

              {/* SMS header — direction + phone + channel, mirrors the email
                  recipients header so the timeline stays visually consistent. */}
              {isSms && event.meta && (
                <div className="text-[11px] text-slate-500 space-y-0.5">
                  {event.meta.phone && (
                    <div>
                      <span className="font-medium">{isSmsOut ? "To:" : "From:"}</span>{" "}
                      <span className="tabular-nums">{event.meta.phone}</span>
                    </div>
                  )}
                  {event.meta.channel === "aircall_dock" && (
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">via Aircall dock</div>
                  )}
                  {event.meta.status && (
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">Status: {event.meta.status}</div>
                  )}
                </div>
              )}

              {/* Non-email, non-SMS body (SMS renders below via its own block) */}
              {!isEmail && !isSms && event.body && (
                <div className="text-xs text-slate-700 whitespace-pre-wrap">{event.body}</div>
              )}
              {!isEmail && !isSms && !event.body && event.summary && (
                <div className="text-xs text-slate-600">{event.summary}</div>
              )}

              {/* SMS body — full text (SMS are naturally short, no lazy-load
                  needed). Direction-tinted background subtly distinguishes
                  outbound from inbound without shouting. */}
              {isSms && (event.body || event.summary) && (
                <div
                  className={`text-xs whitespace-pre-wrap break-words rounded p-2 border ${
                    isSmsOut
                      ? "bg-blue-50 border-blue-200 text-blue-950"
                      : "bg-emerald-50 border-emerald-200 text-emerald-950"
                  }`}
                  data-testid={`sms-body-${isSmsOut ? "out" : "in"}`}
                >
                  {event.body || event.summary}
                </div>
              )}

              {/* Aircall AI: call summary card (populated by webhook or
                  on-demand fetch). Shows summary paragraph, topic chips,
                  sentiment badge, action items, and toggle for the raw
                  transcript. Falls back to a "Fetch AI summary" button
                  when the row has no summary yet. */}
              {isCall && (
                callHasAiSummary ? (
                  <div className="text-xs bg-violet-50 border border-violet-200 rounded p-2 space-y-1.5" data-testid="call-ai-summary">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-violet-700 font-semibold">
                      <Bot className="w-3 h-3" /> Aircall AI summary
                      {event.meta?.ai_sentiment && (
                        <span
                          className={`ml-auto px-1.5 py-0.5 rounded text-[10px] normal-case ${
                            event.meta.ai_sentiment === "positive" ? "bg-emerald-100 text-emerald-800" :
                            event.meta.ai_sentiment === "negative" ? "bg-rose-100 text-rose-800" :
                            "bg-slate-100 text-slate-700"
                          }`}
                          data-testid="call-ai-sentiment"
                        >
                          {event.meta.ai_sentiment}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-800 whitespace-pre-wrap leading-snug">
                      {event.meta.ai_summary}
                    </div>
                    {(event.meta.ai_topics || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-0.5">
                        {event.meta.ai_topics.map((t, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    {(event.meta.ai_action_items || []).length > 0 && (
                      <div className="pt-1 border-t border-violet-200">
                        <div className="text-[10px] uppercase tracking-wide text-violet-700 font-semibold mb-0.5">
                          Action items
                        </div>
                        <ul className="text-xs text-slate-700 space-y-0.5">
                          {event.meta.ai_action_items.map((a, i) => (
                            <li key={i} className="flex gap-1"><span>✓</span><span>{a}</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {event.meta.has_transcript && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setTranscriptOpen(v => !v); }}
                        className="text-[11px] text-violet-700 hover:text-violet-900 hover:underline"
                        data-testid="call-transcript-toggle"
                      >
                        {transcriptOpen ? "Hide transcript ↑" : "Show transcript ↓"}
                      </button>
                    )}
                    {transcriptOpen && (event.meta.transcript || []).length > 0 && (
                      <div className="mt-1 space-y-1 pt-1 border-t border-violet-200 max-h-64 overflow-y-auto" data-testid="call-transcript">
                        {event.meta.transcript.map((u, i) => (
                          <div key={i} className="text-[11px] leading-snug">
                            <span className={`font-semibold ${u.participant_type === "internal" ? "text-blue-700" : "text-emerald-700"}`}>
                              {u.participant_type === "internal" ? "Agent" : "Customer"}:
                            </span>{" "}
                            <span className="text-slate-700">{u.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : event.meta?.aircall_id && !(callHasNoAiData || locallyNoAiData) ? (
                  <button
                    type="button"
                    disabled={fetchingSummary}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        setFetchingSummary(true);
                        const res = await fetchAircallCallAiSummary(caseId, event.meta.aircall_id, { includeTranscript: true });
                        if (res?.ok && (res.summary || res.topics?.length || res.action_items?.length)) {
                          toast.success("AI insights fetched — refreshing timeline");
                          onDeleted?.(event);  // triggers parent refresh
                        } else if (res?.reason === "no_ai_data") {
                          // Silent-ish degrade: short toast, mark this
                          // row as "checked, no data" so the button
                          // hides. Backend also persists the flag so
                          // it survives a page reload.
                          toast("No AI insights on this call", {
                            description: "Aircall didn't generate a summary — probably too short or agent lacks AI licence.",
                            duration: 4000,
                          });
                          setLocallyNoAiData(true);
                        } else {
                          toast.error(res?.message || "Aircall didn't return AI data for this call.");
                        }
                      } catch (err) {
                        const status = err?.response?.status;
                        const detail = err?.response?.data?.detail || err?.message || "";
                        if (status === 424) {
                          toast.error("Aircall AI unavailable: " + detail, { duration: 8000 });
                        } else {
                          toast.error("Fetch failed: " + detail);
                        }
                      } finally {
                        setFetchingSummary(false);
                      }
                    }}
                    className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-violet-50 border border-violet-200 text-violet-800 hover:bg-violet-100 disabled:opacity-60"
                    data-testid="call-fetch-ai-summary"
                  >
                    {fetchingSummary ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
                    {fetchingSummary ? "Fetching…" : "Fetch AI summary"}
                  </button>
                ) : (callHasNoAiData || locallyNoAiData) ? (
                  <div
                    className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200"
                    data-testid="call-no-ai-data"
                    title="Aircall didn't generate any AI insights for this call (usually too short or agent without an AI licence)"
                  >
                    <Bot className="w-2.5 h-2.5" /> No AI insights
                  </div>
                ) : null
              )}

              {/* Audio recording (calls / voicemail) */}
              {event.meta?.recording && (
                <audio controls src={event.meta.recording} className="w-full max-w-sm h-8" />
              )}

              {/* Outlook deep-link */}
              {event.meta?.web_link && (
                <a
                  href={event.meta.web_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open in Outlook →
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Delete / hide confirmation dialog. Notes and P2Ps are hard-deleted
        from the case's own storage; every other event type is soft-hidden
        (`hidden_event_ids`) so the underlying record — external Aircall
        log, M365 email, LoanDisk file, CH filing, etc. — stays intact and
        can be restored later by an admin. */}
    <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {deletable?.kind === "hide"
              ? `Hide this ${deletable?.label} from the timeline?`
              : `Delete this ${deletable?.label}?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {deletable?.kind === "hide" ? (
              <>
                &ldquo;{event.title}&rdquo; will disappear from this case&rsquo;s timeline. The
                underlying record stays in its source system and can be
                restored later by an admin.
              </>
            ) : (
              <>
                This will permanently remove &ldquo;{event.title}&rdquo; from the timeline. {deletable?.kind === "p2p" && "All related P2P events (created/kept/broken/rescheduled) will be removed too."} This can&rsquo;t be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="timeline-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={deleting}
            onClick={handleDelete}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="timeline-delete-confirm"
          >
            {deleting
              ? (deletable?.kind === "hide" ? "Hiding…" : "Deleting…")
              : (deletable?.kind === "hide" ? "Hide" : "Delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Quick-action composer — Call · SMS · Note · P2P
// (Email lives in the dedicated "Inbox" tab — full compose/reply/threading there.)
// ───────────────────────────────────────────────────────────────────────
function QuickActionComposer({ caseRow, onAdded }) {
  const [active, setActive] = useState(null);  // "call" | "sms" | "note" | "p2p" | "event" | "plan" | "bs" | null
  // Emma #16 — several PGs can be in Breathing Space at once; keep the full list.
  const initialBsList = Array.isArray(caseRow?.breathing_spaces)
    ? caseRow.breathing_spaces
    : (caseRow?.breathing_space?.id ? [caseRow.breathing_space] : []);
  const [bsList, setBsList] = useState(initialBsList);
  const activeBs = bsList.filter(b => b?.active);
  const [endingBs, setEndingBs] = useState(null);
  const actions = [
    { id: "call",  label: "Call",       icon: Phone,         testid: "composer-action-call" },
    { id: "sms",   label: "SMS",        icon: MessageSquare, testid: "composer-action-sms" },
    { id: "note",  label: "Note",       icon: StickyNote,    testid: "composer-action-note" },
    { id: "p2p",   label: "P2P",        icon: Target,        testid: "composer-action-p2p" },
    { id: "event", label: "Legal event", icon: CalendarPlus, testid: "composer-action-event" },
    { id: "plan",  label: "Payment plan", icon: HandCoins,   testid: "composer-action-plan" },
    { id: "payout", label: "Payout letter", icon: Receipt,   testid: "composer-action-payout" },
    { id: "statement", label: "Statement", icon: FileText, testid: "composer-action-statement" },
    { id: "pdn", label: "Pre-Default Notice", icon: FileWarning, testid: "composer-action-pdn" },
    { id: "bs",    label: rl("hold"), icon: PauseCircle, testid: "composer-action-bs" },
  ];

  const handleEndBs = async (bs) => {
    const who = bs.subject ? ` for ${bs.subject}` : "";
    if (!window.confirm(`End the ${rl("hold")}${who}? ${activeBs.length > 1 ? "Other guarantors stay protected." : "Collections activity may resume."}`)) return;
    setEndingBs(bs.id);
    try {
      const r = await endBreathingSpace(caseRow.case_id, { actor: "Ops", bs_id: bs.id });
      setBsList(r.breathing_spaces || [r.breathing_space]);
      toast.success(`${rl("hold")} ended${who} — logged to timeline`);
      onAdded?.();
    } catch (e) {
      toast.error("Couldn't end Breathing Space: " + (e.response?.data?.detail || e.message));
    } finally {
      setEndingBs(null);
    }
  };

  return (
    <div className="px-4 py-3" data-testid="quick-action-composer">
      {activeBs.map((bs, idx) => (
        <div key={bs.id || idx} className="mb-3 rounded-md border border-purple-300 bg-purple-50 px-3 py-2 flex items-start justify-between gap-3" data-testid="breathing-space-banner">
          <div className="text-xs text-purple-900">
            <div className="font-semibold flex items-center gap-1.5">
              <PauseCircle className="w-4 h-4" /> BREATHING SPACE ACTIVE — {bs.bs_type === "mental_health" ? "Mental Health Crisis" : "Standard (Debt)"}
              {bs.subject && <span className="ml-1 px-1.5 py-0.5 rounded bg-purple-200 text-purple-900 text-[10px] uppercase tracking-wide" data-testid="bs-banner-subject">{bs.subject}</span>}
              {activeBs.length > 1 && <span className="text-[10px] text-purple-700 font-normal">({idx + 1} of {activeBs.length})</span>}
            </div>
            <div className="mt-0.5">
              Advisor: <strong>{bs.debt_advisor}</strong>
              {bs.reference ? <> · Ref: <strong>{bs.reference}</strong></> : null}
              {" · "}Entered {bs.start_date}
              {bs.end_date ? <> · Ends <strong>{bs.end_date}</strong></> : " · end date TBC"}
            </div>
            {(bs.directors || []).some(d => d.in_bs) && !bs.subject && (
              <div className="mt-1 space-y-0.5" data-testid="bs-director-list">
                {(bs.directors || []).map((d, i) => (
                  <div key={i} className={d.in_bs ? "font-semibold text-purple-900" : "text-purple-700"}>
                    {d.in_bs ? "🔴" : "🟢"} {d.name} — {d.in_bs ? "IN BREATHING SPACE" : "not in breathing space"}
                  </div>
                ))}
              </div>
            )}
            {idx === activeBs.length - 1 && (
              <div className="mt-0.5 text-[11px] text-purple-700">⚠️ All collections contact and enforcement must pause while active.</div>
            )}
          </div>
          <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0" onClick={() => handleEndBs(bs)} disabled={!!endingBs} data-testid="bs-end-btn">
            {endingBs === bs.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Mark ended
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mr-1.5">
          Quick action
        </span>
        {actions.map(a => {
          const Icon = a.icon;
          const isActive = active === a.id;
          return (
            <Button
              key={a.id}
              variant={isActive ? "default" : "outline"}
              size="sm"
              className="h-8 text-xs"
              onClick={() => setActive(isActive ? null : a.id)}
              data-testid={a.testid}
            >
              <Icon className="w-3.5 h-3.5 mr-1.5" /> {a.label}
            </Button>
          );
        })}
      </div>
      {active === "call"  && <CallPanel  caseRow={caseRow} onDone={() => { setActive(null); onAdded?.(); }} />}
      {active === "sms"   && <SmsPanel   caseRow={caseRow} onDone={() => { setActive(null); onAdded?.(); }} />}
      {active === "note"  && <NotePanel  caseRow={caseRow} onDone={() => { setActive(null); onAdded?.(); }} />}
      {active === "p2p"   && <P2PPanel   caseRow={caseRow} onDone={() => { setActive(null); onAdded?.(); }} />}
      {active === "event" && <EventPanel caseRow={caseRow} onDone={() => { setActive(null); onAdded?.(); }} />}
      {active === "bs" && (
        <BreathingSpacePanel
          caseRow={caseRow}
          activeList={activeBs}
          onDone={(r) => { setActive(null); if (r?.breathing_spaces) setBsList(r.breathing_spaces); onAdded?.(); }}
        />
      )}
      {active === "payout" && (
        <PayoutLetterWizard caseRow={caseRow} onClose={() => setActive(null)} onDone={() => { setActive(null); onAdded?.(); }} />
      )}
      {active === "statement" && (
        <StatementDialog caseRow={caseRow} onClose={() => setActive(null)} onDone={() => { setActive(null); onAdded?.(); }} />
      )}
      {active === "pdn" && (
        <GenerateLegalDocDialog kind="pdn" caseRow={caseRow} onClose={() => { setActive(null); onAdded?.(); }} />
      )}
      {active === "plan"  && (
        <PaymentPlanWizard
          caseRow={caseRow}
          onClose={() => setActive(null)}
          onDone={() => { setActive(null); onAdded?.(); }}
        />
      )}
    </div>
  );
}

// ── Legal Event composer — same backend as the Legal Calendar's "Add event"
// dialog, just inline. Pre-scoped to this case, supports auto-followups
// (e.g. SD2 served + 18d challenge + 21d petition).
function EventPanel({ caseRow, onDone }) {
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("");
  const [date, setDate] = useState(() => deskDayKey(new Date().toISOString()));
  const [customLabel, setCustomLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [autoFollowups, setAutoFollowups] = useState(true);
  const [busy, setBusy] = useState(false);

  // Lazy-load the 24-type catalogue once per session — cached via state
  useEffect(() => {
    (async () => {
      try {
        const r = await listLegalEventTypes();
        setTypes(r.types || []);
      } catch (e) {
        // Non-fatal — user will see an empty dropdown and a toast on submit
        console.warn("legal event types fetch failed", e);
      }
    })();
  }, []);

  const selectedType = types.find(t => t.key === type);
  const hasFollowups = !!(selectedType?.auto_followups?.length);

  const save = async () => {
    if (!type) { toast.error("Pick an event type"); return; }
    if (type === "other" && !customLabel.trim()) {
      toast.error("Add a custom label for 'Other' events");
      return;
    }
    if (!date) { toast.error("Pick a date"); return; }
    try {
      setBusy(true);
      const r = await createLegalEvent({
        case_id: caseRow.case_id,
        type,
        date,
        custom_label: type === "other" ? customLabel.trim() : null,
        notes: notes.trim() || null,
        create_auto_followups: autoFollowups,
      });
      toast.success(`📅 Created ${r.created} legal event${r.created === 1 ? "" : "s"}`);
      onDone();
    } catch (e) {
      toast.error("Event failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 p-3 rounded-md border bg-red-50/40 border-red-200 space-y-3" data-testid="composer-panel-event">
      <div className="flex items-center gap-1.5">
        <Scale className="w-4 h-4 text-red-700" />
        <span className="text-sm font-semibold text-red-900">
          Add legal event for {caseRow.customer_name || "this case"}
        </span>
      </div>
      <div className="space-y-3">
        {/* Type */}
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Event type</label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-9 text-sm mt-0.5" data-testid="event-type-select">
              <SelectValue placeholder="— Pick an event type —" />
            </SelectTrigger>
            <SelectContent>
              {types.map(t => (
                <SelectItem key={t.key} value={t.key}>
                  {t.label}{t.auto_followups?.length ? ` (+${t.auto_followups.length} auto)` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {type === "other" && (
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Custom label</label>
            <Input
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="e.g. Application to remove charging order"
              className="h-9 text-sm mt-0.5"
              data-testid="event-custom-label"
            />
          </div>
        )}

        {/* Date */}
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Date</label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 text-sm mt-0.5"
            data-testid="event-date-input"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={500}
            className="w-full text-sm border rounded-md px-2 py-1.5 mt-0.5 resize-none focus:outline-none focus:ring-2 focus:ring-red-500/30"
            data-testid="event-notes"
            placeholder="Anything ops should remember about this event…"
          />
        </div>

        {/* Auto-followups toggle */}
        {hasFollowups && (
          <label className="flex items-start gap-2 text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded p-2">
            <input
              type="checkbox"
              checked={autoFollowups}
              onChange={(e) => setAutoFollowups(e.target.checked)}
              className="mt-0.5"
              data-testid="event-auto-followups"
            />
            <span>
              Also auto-create {selectedType.auto_followups.length} follow-up event{selectedType.auto_followups.length === 1 ? "" : "s"}:{" "}
              {selectedType.auto_followups.map((f, i) => (
                <span key={f.type}>
                  <span className="font-medium">{f.type.replace(/_/g, " ")}</span> (+{f.days}d){i < selectedType.auto_followups.length - 1 ? ", " : ""}
                </span>
              ))}
            </span>
          </label>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground">
          Saved events appear in <strong>Legal Calendar</strong> and the case&apos;s Legal Schedule tab.
        </span>
        <Button onClick={save} disabled={busy} size="sm" className="bg-red-700 hover:bg-red-800" data-testid="event-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CalendarPlus className="w-4 h-4 mr-1" />}
          Add event
        </Button>
      </div>
    </div>
  );
}

// ── Resolve the best phone we know for the customer ────────────────────
// CustomerFile case rows historically use `borrower_phone` but newer
// records use `primary_phone`. Additional contacts (PG, accountant) also
// have phones we can dial. Order: borrower_phone → primary_phone →
// borrower_mobile → first additional_contacts entry with a phone.
function bestCustomerPhone(caseRow) {
  if (!caseRow) return "";
  const direct = caseRow.borrower_phone || caseRow.primary_phone || caseRow.borrower_mobile;
  if (direct) return String(direct).trim();
  const contacts = caseRow.additional_contacts || [];
  for (const c of contacts) {
    if (c && c.phone) return String(c.phone).trim();
  }
  return "";
}

// ── PhonePicker — recipient dropdown sourced from the full contacts list.
// Used by both the Call and SMS quick-action composers so reps can pick
// any director / PG / accountant / landline on the file in one click. A
// "Custom" item at the bottom reveals a free-text input for one-off dials.
function PhonePicker({ caseRow, value, onChange, testId }) {
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customMode, setCustomMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getAgeingContacts(caseRow.case_id);
        const list = (r?.contacts || []).filter(c => c.phone && String(c.phone).trim());
        setContacts(list);
      } catch {
        setContacts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [caseRow.case_id]);

  // Normalise a phone for comparison
  const norm = (p) => String(p || "").replace(/[^\d+]/g, "");

  // Build the option list — de-dupe by normalised phone
  const seen = new Set();
  const options = [];
  for (const c of (contacts || [])) {
    const k = norm(c.phone);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    const isPrimary = (c.role || "").toLowerCase().includes("primary");
    options.push({
      phone: c.phone,
      label: c.name || c.role || "Contact",
      role:  c.role || "Contact",
      isPrimary,
      source: c.source,
    });
  }

  // Also surface case-level phones not already in the contacts list
  const extras = [
    { phone: caseRow.primary_phone,  label: caseRow.customer_name || "Primary mobile", role: "Primary mobile" },
    { phone: caseRow.borrower_phone, label: caseRow.customer_name || "Borrower",      role: "Borrower" },
  ];
  for (const e of extras) {
    const k = norm(e.phone);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    options.push({ phone: e.phone, label: e.label, role: e.role, isPrimary: false });
  }

  // Sort: primary first, then directors, then everyone else by role
  options.sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    const ra = (a.role || "").toLowerCase();
    const rb = (b.role || "").toLowerCase();
    if (ra.includes("director") !== rb.includes("director")) return ra.includes("director") ? -1 : 1;
    return ra.localeCompare(rb);
  });

  const currentMatch = options.find(o => norm(o.phone) === norm(value));

  if (customMode || (!loading && options.length === 0)) {
    return (
      <div className="flex items-center gap-1">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="+44 7…"
          className="h-8 text-xs flex-1"
          data-testid={testId}
        />
        {options.length > 0 && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-[10px]" onClick={() => setCustomMode(false)}>
            ← from list
          </Button>
        )}
      </div>
    );
  }

  return (
    <Select
      value={currentMatch ? norm(currentMatch.phone) : "__custom__"}
      onValueChange={(v) => {
        if (v === "__custom__") { setCustomMode(true); return; }
        const opt = options.find(o => norm(o.phone) === v);
        if (opt) onChange(opt.phone);
      }}
      disabled={loading}
    >
      <SelectTrigger className="h-8 text-xs" data-testid={testId}>
        <SelectValue placeholder={loading ? "Loading contacts…" : "Pick a recipient"}>
          {currentMatch ? (
            <span className="truncate">
              <span className="font-medium">{currentMatch.label}</span>
              <span className="text-muted-foreground"> · {currentMatch.role}</span>
              <span className="text-muted-foreground"> · {currentMatch.phone}</span>
            </span>
          ) : value ? (
            <span>📝 Custom · {value}</span>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-w-[460px]">
        {options.map(o => (
          <SelectItem key={norm(o.phone)} value={norm(o.phone)} className="text-xs">
            <div className="flex items-center gap-2 max-w-full">
              <span className="font-medium truncate">{o.label}</span>
              <span className="text-[10px] uppercase text-muted-foreground tracking-wide">{o.role}</span>
              <span className="text-muted-foreground tabular-nums">{o.phone}</span>
              {o.isPrimary && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1 rounded">primary</span>}
            </div>
          </SelectItem>
        ))}
        <SelectItem value="__custom__" className="text-xs italic">
          📝 Custom number — type manually
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

// ── Call (Aircall — portal-embedded dock OR desktop click-to-dial) ─────
function CallPanel({ caseRow, onDone }) {
  const aircall = useAircall();
  const [mode, setMode] = useState("embedded");  // "embedded" | "desktop"
  const [users, setUsers] = useState([]);
  const [lines, setLines] = useState([]);
  const [userId, setUserId] = useState("");
  const [lineId, setLineId] = useState("");
  const [toNumber, setToNumber] = useState(bestCustomerPhone(caseRow));
  const [busy, setBusy] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);

  useEffect(() => {
    // Only fetch desktop-mode metadata (users + lines) when desktop mode
    // is selected — saves a slow Aircall API roundtrip on the common
    // happy path (portal dock dial).
    if (mode !== "desktop") {
      setLoadingMeta(false);
      return;
    }
    if (users.length > 0 || lines.length > 0) return;  // cached from previous switch
    setLoadingMeta(true);
    (async () => {
      const [usersRes, linesRes] = await Promise.allSettled([
        aircallListUsers(),
        getAircallUKSmsLines(),
      ]);
      if (usersRes.status === "fulfilled") {
        const us = usersRes.value.users || [];
        setUsers(us);
        const me = us.find(x => x.available) || us[0];
        if (me) setUserId(String(me.id));
      } else {
        toast.error("Could not load Aircall users: " + (usersRes.reason?.response?.data?.detail || usersRes.reason?.message || ""));
      }
      if (linesRes.status === "fulfilled") {
        const ls = linesRes.value.lines || [];
        setLines(ls);
        const firstLine = ls.find(x => x.public_api_sms) || ls[0];
        if (firstLine) setLineId(String(firstLine.id));
      } else {
        toast.error("Could not load Aircall lines: " + (linesRes.reason?.response?.data?.detail || linesRes.reason?.message || ""));
      }
      setLoadingMeta(false);
    })();
  }, [mode, users.length, lines.length]);

  const dial = async () => {
    if (!toNumber) {
      toast.error("Enter a number to dial");
      return;
    }
    setBusy(true);
    try {
      if (mode === "embedded") {
        // Drive the Aircall Everywhere workspace embedded in the dock.
        // The SDK rings inside the portal — no desktop app needed.
        if (!aircall?.ready) {
          toast.error("Aircall workspace isn't loaded yet. Try the Aircall app option instead.");
          setBusy(false);
          return;
        }
        // Don't block on stale `loggedIn` — the SDK probes session via
        // cookie and `onLogin` doesn't always fire on auto-resume. Just
        // try dialling; SDK will return `not_logged` if it actually fails.
        aircall.expand();
        aircall.dial(toNumber, {
          onSuccess: () => {
            toast.success("📞 Ringing inside the Aircall dock.");
            onDone();
          },
          onError: () => {/* toast already shown by provider */},
        });
      } else {
        if (!userId || !lineId) {
          toast.error("Pick a user and a line to dial from");
          setBusy(false);
          return;
        }
        await aircallClickToDial(caseRow.case_id, {
          to_number: toNumber,
          user_id:   parseInt(userId, 10),
          from_number_id: parseInt(lineId, 10),
          actor: "Ops",
        });
        toast.success("📞 Dial request sent — accept it on your Aircall app to ring out.");
        onDone();
      }
    } catch (e) {
      toast.error("Click-to-dial failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const isEmbedded = mode === "embedded";

  return (
    <div className="mt-3 p-3 rounded-md border bg-blue-50/40 border-blue-200 space-y-2" data-testid="composer-panel-call">
      {/* Mode toggle ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 text-[10px]">
        <span className="uppercase tracking-wide text-muted-foreground font-semibold">Ring via</span>
        <div className="inline-flex rounded-md border border-blue-200 bg-white p-0.5">
          <button
            type="button"
            onClick={() => setMode("embedded")}
            data-testid="call-mode-embedded"
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition ${
              isEmbedded ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            📞 Portal dock
          </button>
          <button
            type="button"
            onClick={() => setMode("desktop")}
            data-testid="call-mode-desktop"
            className={`px-2 py-0.5 rounded text-[10px] font-medium transition ${
              !isEmbedded ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Aircall app
          </button>
        </div>
      </div>

      {/* Inputs ─────────────────────────────────────────────────────── */}
      {isEmbedded ? (
        <div className="grid grid-cols-1 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">To</label>
            <PhonePicker
              caseRow={caseRow}
              value={toNumber}
              onChange={setToNumber}
              testId="call-to-input"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">As user</label>
            <Select value={userId} onValueChange={setUserId} disabled={loadingMeta}>
              <SelectTrigger className="h-8 text-xs" data-testid="call-user-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.id} value={String(u.id)} className="text-xs">
                    {u.name} {u.available ? "🟢" : "⚫"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">From line</label>
            <Select value={lineId} onValueChange={setLineId} disabled={loadingMeta}>
              <SelectTrigger className="h-8 text-xs" data-testid="call-line-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {lines.map(l => (
                  <SelectItem key={l.id} value={String(l.id)} className="text-xs">
                    {l.name || l.digits}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">To</label>
            <PhonePicker
              caseRow={caseRow}
              value={toNumber}
              onChange={setToNumber}
              testId="call-to-input"
            />
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground">
          {isEmbedded
            ? "Rings inside the Aircall dock at the bottom-right of the portal."
            : "Aircall pushes the dial to your desktop/mobile app. Pick it up to ring out."}
        </p>
        <Button onClick={dial} disabled={busy || (!isEmbedded && loadingMeta)} size="sm" data-testid="call-dial-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Phone className="w-3.5 h-3.5 mr-1.5" />}
          Dial
        </Button>
      </div>
    </div>
  );
}

// ── SMS — mirrors the Call UX: portal-dock mode (opens the workspace
// messaging panel for the contact, rep types body manually inside the
// Aircall iframe) OR backend send (sends via the dedicated APISMS line,
// timeline picks up the response via webhook). Backend send is the
// default because it's fully automated and replies are tracked.
// ── Breathing Space panel — Debt Respite Scheme logging ─────────────────
function BreathingSpacePanel({ caseRow, activeList = [], onDone }) {
  const today = deskDayKey(new Date().toISOString());
  const plus60 = deskDayKey(new Date(Date.now() + 60 * 86400000).toISOString());
  const [advisor, setAdvisor] = useState("");
  const [reference, setReference] = useState("");
  const [bsType, setBsType] = useState("standard");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(plus60);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  // Emma #16 — one BS per guarantor; suggest known names, free text allowed.
  const knownNames = Array.from(new Set([
    caseRow?.borrower_director_name,
    ...(caseRow?.guarantors || []).map(g => g?.name),
    ...(caseRow?.additional_contacts || []).map(c => c?.name),
  ].map(n => (n || "").trim()).filter(Boolean)));
  const protectedKeys = new Set(activeList.map(b => (b.subject || "").trim().toLowerCase()));
  const [subject, setSubject] = useState(() => knownNames.find(n => !protectedKeys.has(n.toLowerCase())) || "");

  const changeType = (t) => {
    setBsType(t);
    // Standard BS = 60 days; Mental Health lasts as long as crisis treatment (+30d) — end date optional
    if (t === "standard" && !endDate) setEndDate(plus60);
    if (t === "mental_health") setEndDate("");
  };

  const save = async () => {
    if (!advisor.trim()) { toast.error("Debt advisor is required"); return; }
    if (!startDate) { toast.error("Start date is required"); return; }
    if (!subject.trim()) { toast.error("Say who this Breathing Space protects (guarantor / director name)"); return; }
    if (protectedKeys.has(subject.trim().toLowerCase())) { toast.error(`${subject.trim()} is already in Breathing Space — end that one first`); return; }
    setBusy(true);
    try {
      const r = await startBreathingSpace(caseRow.case_id, {
        debt_advisor: advisor.trim(),
        reference: reference.trim() || undefined,
        bs_type: bsType,
        start_date: startDate,
        end_date: endDate || undefined,
        notes: notes.trim() || undefined,
        subject: subject.trim(),
        directors: [{ name: subject.trim(), in_bs: true }],
        actor: "Ops",
      });
      toast.success(`⏸️ Breathing Space logged for ${subject.trim()} — posted to timeline` + (endDate ? " and legal calendar" : ""));
      onDone(r);
    } catch (e) {
      toast.error("Couldn't log Breathing Space: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 p-3 rounded-md border bg-purple-50/40 border-purple-200 space-y-2" data-testid="composer-panel-bs">
      <div className="text-[10px] text-muted-foreground">
        Logs the Breathing Space on this case, posts it to the timeline, and (when an end date is set)
        drops the deadline onto the <strong>legal calendar</strong>. Collections activity must pause while active.
        {activeList.length > 0 && (
          <span className="block mt-0.5 text-purple-800" data-testid="bs-panel-already-protected">
            Already protected: <strong>{activeList.map(b => b.subject || "unnamed").join(", ")}</strong> — log another guarantor below.
          </span>
        )}
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Who is in Breathing Space? *</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} list="bs-subject-suggestions"
          placeholder="Guarantor / director name" className="h-8 text-xs" data-testid="bs-subject-input" />
        <datalist id="bs-subject-suggestions">
          {knownNames.map(n => <option key={n} value={n} />)}
        </datalist>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Debt advisor *</label>
          <Input value={advisor} onChange={(e) => setAdvisor(e.target.value)} placeholder="e.g. StepChange / Citizens Advice" className="h-8 text-xs" data-testid="bs-advisor-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">BS reference</label>
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Insolvency Service ref" className="h-8 text-xs" data-testid="bs-reference-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Type *</label>
          <Select value={bsType} onValueChange={changeType}>
            <SelectTrigger className="h-8 text-xs" data-testid="bs-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard (Debt) — 60 days</SelectItem>
              <SelectItem value="mental_health">Mental Health Crisis — open-ended</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Entered *</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8 text-xs" data-testid="bs-start-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Ends{bsType === "mental_health" ? " (optional)" : ""}</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-8 text-xs" data-testid="bs-end-input" />
          </div>
        </div>
      </div>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes (e.g. advisor contact details, conditions)…" rows={2} className="text-xs" data-testid="bs-notes-input" />
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={busy} data-testid="bs-save-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <PauseCircle className="w-3.5 h-3.5 mr-1.5" />}
          Log Breathing Space
        </Button>
      </div>
    </div>
  );
}


// ── Breathing Space end ── (helpers used by the banner live in QuickActionComposer)

function SmsPanel({ caseRow, onDone }) {
  const aircall = useAircall();
  // Dock-only sending — the old "Portal (APISMS-UK)" mode was removed: API
  // sends showed as "APISMS" with no user attribution. Templates + preview
  // stay; the only send action is Copy & open dock.
  const [toNumber, setToNumber] = useState(bestCustomerPhone(caseRow));
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  // Chaser template state — templates fetched on mount, rendered on
  // selection so the ops user sees the resolved copy (not raw
  // {placeholders}) in the textarea and can tweak before sending.
  const [templates, setTemplates]         = useState([]);
  const [placeholders, setPlaceholders]   = useState([]);
  const [groupLabels, setGroupLabels]     = useState({});
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [pickedTemplateId, setPickedTemplateId] = useState("");
  // Live preview state — debounced re-render every time the body
  // changes so ops sees exactly what the customer will receive. Powers
  // the "as customer will see it" panel below the textarea. Also flags
  // any {unknown_token} the composer chips don't cover so the user
  // catches typos like `{name}` at composition, not at delivery.
  const [preview, setPreview]                   = useState({ rendered: "", chars: 0, segments: 0, unsupported: [] });
  const [previewBusy, setPreviewBusy]           = useState(false);
  const textareaRef                       = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await listChaserSmsTemplates({ onlyActive: true });
        setTemplates(r.templates || []);
        setPlaceholders(r.placeholders || []);
        setGroupLabels(r.group_labels || {});
      } catch (e) {
        console.warn("Could not load chaser templates:", e?.message);
      } finally {
        setTemplatesLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const raw = body.trim();
    if (!raw) {
      setPreview({ rendered: "", chars: 0, segments: 0, unsupported: [] });
      return;
    }
    let cancelled = false;
    setPreviewBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await previewChaserSmsForCase(caseRow.case_id, body);
        if (cancelled) return;
        setPreview({
          rendered:    r.rendered || "",
          chars:       r.chars || 0,
          segments:    r.segments || 0,
          unsupported: r.unsupported_tokens || [],
        });
      } catch (e) {
        if (!cancelled) console.warn("SMS preview failed:", e?.message);
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [body, caseRow.case_id]);

  // ── Template pick — render server-side against this case, drop into
  // the textarea. Ops can then edit before sending.
  const handleTemplatePick = async (templateId) => {
    if (!templateId) return;
    setPickedTemplateId(templateId);
    try {
      const r = await renderChaserSmsForCase(caseRow.case_id, templateId);
      setBody(r.rendered || "");
      toast.success(`Loaded ${r.name} · ${r.chars} chars · ${r.segments} segment${r.segments === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Failed to load template: " + (e?.response?.data?.detail || e?.message));
      setPickedTemplateId("");
    }
  };

  // ── Placeholder chip → insert token at the current cursor position
  // in the body textarea. Works for both template-loaded and blank
  // composers so ops can paste {sf_number} into their own custom copy.
  const insertPlaceholder = (token) => {
    const el = textareaRef.current;
    if (!el) {
      setBody(prev => prev + token);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end   = el.selectionEnd   ?? body.length;
    const next  = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // Move cursor to after the inserted token — after React re-renders.
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const pos = start + token.length;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  // Group templates by chaser # for the dropdown. Sort inside each
  // group by tone (a < b < c).
  const groupedTemplates = useMemo(() => {
    const groups = {};
    (templates || []).forEach(t => {
      const g = t.group || "?";
      if (!groups[g]) groups[g] = [];
      groups[g].push(t);
    });
    Object.values(groups).forEach(arr => arr.sort((a, b) => (a.tone || "").localeCompare(b.tone || "")));
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  }, [templates]);

  // ── Copy & Open Dock ──────────────────────────────────────────────────
  // Preferred workflow for the "dock-first" model:
  //   1. Render placeholders locally (we already have `body` = rendered)
  //   2. Push a note onto the case NOW (before dock takes over) so the
  //      timeline reflects the send even if the dock composer is
  //      slightly edited before the user hits Send inside Aircall.
  //   3. Copy the rendered body to the clipboard so it's a single ⌘V
  //      inside the dock.
  //   4. Pop the dock with the recipient prefilled — Aircall opens on
  //      Calls tab by default; the user flips to the 💬 SMS tab and
  //      pastes.
  const copyAndOpenDock = async () => {
    if (!toNumber) { toast.error("Pick a recipient"); return; }
    if (!body.trim()) { toast.error("Body is empty — nothing to copy"); return; }
    if (!aircall?.ready) { toast.error("Aircall dock isn't loaded yet."); return; }
    setBusy(true);
    try {
      // Best-effort: pre-log the intent so the note lands even if the
      // dock flow fails after this point.
      try {
        await logDockSmsIntent(caseRow.case_id, {
          to:    toNumber,
          body:  body.trim(),
          actor: "Ops",
        });
      } catch (err) {
        console.warn("Dock intent log failed:", err?.message);
      }
      // Copy the rendered body to the clipboard. Fallback for browsers
      // where the clipboard API is blocked (rare in Chromium/Electron).
      const text = body.trim();
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // Legacy fallback via a hidden textarea
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity  = "0";
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch { /* give up */ }
        document.body.removeChild(ta);
      }
      aircall.expand();
      aircall.dial(toNumber, {
        onSuccess: () => {
          toast("📋 Copied to clipboard · dock is open — press ⌘V (or Ctrl+V) inside the SMS tab and hit Send", { duration: 8000 });
          onDone();
        },
        onError: () => { /* provider already toasted */ },
      });
    } finally {
      setBusy(false);
    }
  };

  // Open the dock without a composed body — pre-fills the recipient and
  // logs the intent so the timeline reflects the conversation.
  const openDock = async () => {
    if (!toNumber) {
      toast.error("Pick a recipient");
      return;
    }
    if (!aircall?.ready) {
      toast.error("Aircall dock isn't loaded yet — give it a second and retry.");
      return;
    }
    try {
      await logDockSmsIntent(caseRow.case_id, {
        to:    toNumber,
        body:  body?.trim() || undefined,
        actor: "Ops",
      });
    } catch (err) {
      console.warn("Dock SMS intent log failed:", err?.message);
    }
    aircall.expand();
    aircall.dial(toNumber, {
      onSuccess: () => {
        toast("📂 Opened in Aircall dock — click the 💬 messaging icon there to compose. A note has been added to this case.", { duration: 7000 });
        onDone();
      },
      onError: () => {/* provider already toasted */},
    });
  };

  return (
    <div className="mt-3 p-3 rounded-md border bg-violet-50/40 border-violet-200 space-y-2" data-testid="composer-panel-sms">
      <div className="text-[10px] text-muted-foreground">
        SMS is sent from <strong>your own Aircall number</strong> via the dock — compose here (templates below),
        then <strong>Copy &amp; open dock</strong> and paste. Replies flow back onto the timeline automatically.
      </div>

      {/* Recipient — same dynamic picker as Calls */}
      <div>
        <label className="text-[10px] uppercase tracking-wide text-muted-foreground">To</label>
        <PhonePicker
          caseRow={caseRow}
          value={toNumber}
          onChange={setToNumber}
          testId="sms-to-input"
        />
      </div>

      {/* ─── Template picker + placeholder chips ─── */}
      <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
              Use template
            </label>
            <Select value={pickedTemplateId} onValueChange={handleTemplatePick}>
              <SelectTrigger
                className="h-7 text-[11px] w-[220px] bg-white"
                data-testid="sms-template-picker"
              >
                <SelectValue placeholder={templatesLoading ? "Loading…" : "— Pick a chaser —"} />
              </SelectTrigger>
              <SelectContent>
                {groupedTemplates.map(([grp, tpls]) => (
                  <div key={grp}>
                    <div className="px-2 py-1 text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">
                      {groupLabels[String(grp)] || `Chaser ${grp}`}
                    </div>
                    {tpls.map(t => (
                      <SelectItem
                        key={t.template_id}
                        value={t.template_id}
                        data-testid={`sms-template-opt-${t.template_id}`}
                      >
                        {t.name} · <span className="capitalize text-muted-foreground">{t.tone}</span>
                      </SelectItem>
                    ))}
                  </div>
                ))}
                {!templatesLoading && templates.length === 0 && (
                  <div className="px-3 py-2 text-[11px] text-muted-foreground italic">
                    No templates yet — set them up in Admin Settings.
                  </div>
                )}
              </SelectContent>
            </Select>
            {pickedTemplateId && (
              <button
                type="button"
                onClick={() => { setPickedTemplateId(""); setBody(""); }}
                className="text-[10px] text-slate-500 hover:text-rose-700 underline underline-offset-2"
                data-testid="sms-template-clear"
              >
                clear
              </button>
            )}
          </div>

          {/* Placeholder chip strip — click to insert at cursor */}
          {placeholders.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap" data-testid="sms-placeholder-chips">
              <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Insert:</span>
              {placeholders.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  className="px-1.5 py-0.5 rounded border border-violet-200 bg-white hover:bg-violet-100 text-[10px] font-mono text-violet-700 transition-colors"
                  title={`${p.label} — example: ${p.example}`}
                  data-testid={`sms-placeholder-${p.key.replace(/[{}]/g, "")}`}
                >
                  {p.key}
                </button>
              ))}
            </div>
          )}
        </div>

      {/* Body — composed here, pasted into the dock */}
      <Textarea
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Type your SMS… or pick a template above (160 chars per segment)"
        rows={3}
        className="text-xs"
        data-testid="sms-body-input"
      />

      {/* ── Live preview — "as customer will see it" ─────────────────
          Server-side rendered so preview + delivery never drift.
          Shows a soft warning if the composer contains any tokens
          outside the 7 supported placeholders (e.g. `{name}`).      */}
      {body.trim() && (
        <div
          className="rounded-md border border-violet-200 bg-white/70 px-2.5 py-2 space-y-1"
          data-testid="sms-preview-panel"
        >
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">
              Preview — as customer will see it
            </span>
            {previewBusy && <Loader2 className="w-3 h-3 animate-spin text-violet-400" />}
          </div>
          <div
            className="text-[11px] leading-snug text-slate-800 whitespace-pre-wrap font-mono"
            data-testid="sms-preview-body"
          >
            {preview.rendered || <span className="italic text-slate-400">rendering…</span>}
          </div>
          {preview.unsupported.length > 0 && (
            <div
              className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-1"
              data-testid="sms-preview-unsupported-warning"
            >
              ⚠️ Unsupported token{preview.unsupported.length > 1 ? "s" : ""}:{" "}
              {preview.unsupported.map(t => (
                <code key={t} className="font-mono">{`{${t}}`}</code>
              )).reduce((prev, curr) => prev === null ? [curr] : [...prev, ", ", curr], null)}
              . This will send literally. Use the chips above instead.
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {body.trim()
            ? `${preview.chars || body.length} chars · ${preview.segments || Math.max(1, Math.ceil(body.length / 160))} segment${(preview.chars || body.length) > 160 ? "s" : ""} · sent from your Aircall number`
            : "Compose above, or open the dock to type there"}
        </span>
        <div className="flex items-center gap-2">
          {body.trim() ? (
            <Button
              onClick={copyAndOpenDock}
              disabled={busy}
              size="sm"
              title="Renders placeholders, copies to clipboard, drops a note on the case, and pops the Aircall dock ready to paste"
              data-testid="sms-copy-and-dock-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
              Copy &amp; open dock
            </Button>
          ) : (
            <Button onClick={openDock} disabled={busy} size="sm" variant="outline" data-testid="sms-send-btn">
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
              Open in Aircall
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Email — links straight to the existing Customer Inbox tab so the
// full Outlook split-pane handles the composition (we don't duplicate).
// ── Note ────────────────────────────────────────────────────────────────
function NotePanel({ caseRow, onDone }) {
  const [text, setText] = useState("");
  const [markExternal, setMarkExternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) {
      toast.error("Add some text first");
      return;
    }
    try {
      setBusy(true);
      await addAgeingNote(caseRow.case_id, {
        text: text.trim(),
        author: "Ops",
        audience: markExternal ? "external" : null,   // null = let keyword classifier decide
      });
      toast.success(markExternal ? "🗒 Note added · marked external" : "🗒 Note added");
      setText("");
      setMarkExternal(false);
      onDone();
    } catch (e) {
      toast.error("Note failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 p-3 rounded-md border bg-amber-50/40 border-amber-200 space-y-2" data-testid="composer-panel-note">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What happened? (e.g. Left VM, customer to call back at 3pm)"
        rows={3}
        className="text-xs"
        autoFocus
        data-testid="note-text-input"
      />
      <div className="flex items-center justify-between gap-3">
        <label
          className="flex items-center gap-2 text-[11px] cursor-pointer select-none"
          title="Tick to force this note into the External-friendly (court-ready) view. Otherwise it's auto-classified by keywords (e.g. notes mentioning 'Customer', 'Call:', 'Letter sent' become external automatically)."
        >
          <input
            type="checkbox"
            checked={markExternal}
            onChange={(e) => setMarkExternal(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            data-testid="note-mark-external-checkbox"
          />
          <span className={markExternal ? "font-medium text-emerald-700" : "text-muted-foreground"}>
            Mark as external (court-friendly)
          </span>
        </label>
        <Button onClick={save} disabled={busy} size="sm" data-testid="note-save-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <StickyNote className="w-3.5 h-3.5 mr-1.5" />}
          Save note
        </Button>
      </div>
    </div>
  );
}

// ── Promise to Pay ──────────────────────────────────────────────────────
function P2PPanel({ caseRow, onDone }) {
  const [amount, setAmount] = useState(caseRow.arrears_amount ? String(caseRow.arrears_amount) : "");
  const [date, setDate] = useState(null);
  const [method, setMethod] = useState("bank_transfer");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const save = async () => {
    if (!amount || !date) {
      toast.error("Amount and promise date required");
      return;
    }
    try {
      setBusy(true);
      // Local-date key, NOT toISOString() — a calendar pick is local midnight,
      // which toISOString() rolls back to YESTERDAY in UK summer time (BST).
      const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      await createAgeingP2P(caseRow.case_id, {
        promised_amount: parseFloat(amount),
        promised_at:     isoDate,
        method,
        notes,
        created_by:      "Ops",
      });
      toast.success("P2P scheduled");
      onDone();
    } catch (e) {
      toast.error("P2P failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };
  // Quick-pick chips for common P2P dates (today / +3 / +7 / next pay-Friday)
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const nextFriday = (() => {
    const x = new Date(today);
    const dow = x.getDay();          // 0=Sun … 5=Fri
    const delta = (5 - dow + 7) % 7 || 7;
    x.setDate(x.getDate() + delta);
    return x;
  })();
  const quickPicks = [
    { label: "Today",   d: today },
    { label: "+3 days", d: addDays(today, 3) },
    { label: "+7 days", d: addDays(today, 7) },
    { label: "Next Fri", d: nextFriday },
  ];
  const fmtDate = (d) => d ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "Pick a date";
  return (
    <div className="mt-3 p-3 rounded-md border bg-emerald-50/40 border-emerald-200 space-y-2" data-testid="composer-panel-p2p">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Amount {cur()}</label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 text-xs" data-testid="p2p-amount-input" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Promise date</label>
          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`w-full h-8 justify-start text-xs font-normal ${date ? "text-slate-800" : "text-muted-foreground"}`}
                data-testid="p2p-date-input"
              >
                <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                {fmtDate(date)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-2 border-b flex flex-wrap gap-1">
                {quickPicks.map(q => (
                  <Button
                    key={q.label}
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-[10px]"
                    onClick={() => { setDate(q.d); setCalOpen(false); }}
                    data-testid={`p2p-quick-${q.label.replace(/[^a-z0-9]/gi, '').toLowerCase()}`}
                  >
                    {q.label}
                  </Button>
                ))}
              </div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { setDate(d); if (d) setCalOpen(false); }}
                disabled={(d) => d < addDays(today, -1)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Method</label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bank_transfer" className="text-xs">Bank transfer</SelectItem>
              <SelectItem value="card" className="text-xs">Card payment</SelectItem>
              <SelectItem value="direct_debit" className="text-xs">Direct debit</SelectItem>
              <SelectItem value="cash" className="text-xs">Cash</SelectItem>
              <SelectItem value="other" className="text-xs">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="h-8 text-xs" data-testid="p2p-notes-input" />
      <div className="flex items-center justify-end">
        <Button onClick={save} disabled={busy} size="sm" data-testid="p2p-save-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Target className="w-3.5 h-3.5 mr-1.5" />}
          Schedule P2P
        </Button>
      </div>
    </div>
  );
}
